import React, { useEffect, useContext, useRef } from "react";
import '@viedoc/viedoc-ui/viedoc-inactivity-tracker-cross-domain';
import { useAuth } from "react-oidc-context";
import { useLocation } from "react-router";
import { BusyStateContext } from "~/context/BusyStateContext";
import { useSignOut } from "~/hooks/useSignout";

const KEEP_USER_ACTIVE_INTERVAL = 10 * 1000; // 10s
const heartBeatUrl = `${import.meta.env.VITE_ME_IDP_URL}/api/heartbeat`;
const sessionMonitor = `${import.meta.env.VITE_ME_IDP_URL}/check-session`;

export function InactivityTracker() {
  const { hasBusyItems } = useContext(BusyStateContext);
  const { isAuthenticated, user } = useAuth();
  const signout = useSignOut();
  const { pathname } = useLocation();
  const inactivityTracker = useRef(null);

  useEffect(() => {
    const tracker = inactivityTracker.current;
    if (tracker) {
      tracker.tokenCallback = () => {
        return user?.access_token;
      };

      tracker.logoutCallback = signout;

      return () => {
        if (tracker) {
          // biome-ignore lint/performance/noDelete: Don't expect this to run often enough to be a performance issue
          delete tracker.tokenCallback;
          // biome-ignore lint/performance/noDelete: Don't expect this to run often enough to be a performance issue
          delete tracker.logoutCallback;
        }
      };
    }
  }, [signout, user]);

  const isPdfMode = pathname.startsWith("/pdf/");
  // /preview is a Designer-driven, unauthenticated iframe route (mounted
  // outside RequireAuth in ApplicationRoutes). A stale OIDC user in
  // localStorage from a prior Me session makes `isAuthenticated` return true
  // here even though the route itself doesn't need or use auth — without this
  // bail-out the tracker would auto-log-out a designer who leaves the preview
  // open. See docs/features/2-preview-route.md.
  const isPreviewMode = pathname.startsWith("/preview");

  if (!isAuthenticated || isPdfMode || isPreviewMode) {
    return null;
  }

  return (
    <viedoc-inactivity-tracker-cross-domain
      ref={inactivityTracker}
      heartbeat-url={heartBeatUrl}
      session-monitor-url={sessionMonitor}
      is-single-page
      hot-reload
      keep-alive={hasBusyItems || null}
      keep-user-active-interval={
        hasBusyItems ? KEEP_USER_ACTIVE_INTERVAL : null
      }
    />
  );
}
